import { convertObjectToCamelCase } from '@/helpers'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  InventoryListWithPagesProps,
  InventoryProps,
  InventoryTransactionProps,
} from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import {
  BFF_INVENTORIES_URL,
  BFF_INVENTORY_TRANSACTION_URL,
  BFF_INVENTORY_TRANSACTIONS_URL,
  BFF_INVENTORY_URL,
} from './api.urls'

export const useInventoryList = ({
  params,
}: {
  params: Record<string, string | number>
}) => {
  const getInventoryList = async (): Promise<{
    success: boolean
    message: string
    data?: InventoryListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      if (copyParams.customerLevel === 'ALL') {
        delete copyParams.customerLevel
      }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_INVENTORIES_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Inventorys fetched successfully!',
        data: convertObjectToCamelCase(data) as InventoryListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get inventorys!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.INVENTORY.module, params],
    queryFn: () => getInventoryList(),
  })
}

export const useInventoryDetail = (uuid?: string) => {
  const getInventoryDetail = async (): Promise<{
    success: boolean
    message: string
    data?: InventoryProps
  }> => {
    try {
      const { data } = await api.get(`${BFF_INVENTORY_URL(uuid || '')}`)

      return {
        success: true,
        message: 'Inventory fetched successfully!',
        data: convertObjectToCamelCase(data) as InventoryProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get inventory!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.INVENTORY.module, uuid],
    queryFn: () => getInventoryDetail(),
    enabled: !!uuid,
  })
}

export const useInventoryTransactionList = ({
  params,
}: {
  params: Record<string, string | number>
}) => {
  const getInventoryTransactionList = async (): Promise<{
    success: boolean
    message: string
    data?: InventoryListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      if (copyParams.customerLevel === 'ALL') {
        delete copyParams.customerLevel
      }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_INVENTORY_TRANSACTIONS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Inventory transactions fetched successfully!',
        data: convertObjectToCamelCase(data) as InventoryListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get inventory transactions!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.INVENTORY_TRANSACTION.module, params],
    queryFn: () => getInventoryTransactionList(),
  })
}

export const useInventoryTransactionDetail = (uuid?: string) => {
  const getInventoryTransactionDetail = async (): Promise<{
    success: boolean
    message: string
    data?: InventoryTransactionProps
  }> => {
    try {
      const { data } = await api.get(
        `${BFF_INVENTORY_TRANSACTION_URL(uuid || '')}`,
      )

      return {
        success: true,
        message: 'Inventory transaction fetched successfully!',
        data: convertObjectToCamelCase(data) as InventoryTransactionProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get inventory transaction!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.INVENTORY_TRANSACTION.module, uuid],
    queryFn: () => getInventoryTransactionDetail(),
    enabled: !!uuid,
  })
}

export const useUpdateInventoryTransaction = () => {
  const updateInventoryTransaction = async (
    payload: Partial<InventoryTransactionProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: InventoryTransactionProps
  }> => {
    try {
      const { data } = await api.patch(
        `${BFF_INVENTORY_TRANSACTIONS_URL}${payload.uuid}/`,
        { ...payload, uuid: undefined },
      )

      return {
        success: true,
        message: 'Inventory tranasaction updated successfully!',
        data: convertObjectToCamelCase(data) as InventoryTransactionProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update Inventory tranasaction!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.INVENTORY_TRANSACTION.module],
    mutationFn: (payload: Partial<InventoryTransactionProps>) =>
      updateInventoryTransaction(payload),
  })
}
